import { useEffect, useState } from "react";
import { Clock, Loader2, Play, Star } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, posterUrl } from "@/lib/api";
import { WatchOptions } from "@/components/WatchOptions";

const fmtRuntime = (r) => (r ? `${Math.floor(r / 60)}h ${r % 60}m` : null);

function CastRow({ cast }) {
  if (!cast?.length) return null;
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">Cast</p>
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="details-cast">
        {cast.map((c) => (
          <div key={c.name + c.character} className="w-20 shrink-0">
            <div className="h-24 w-20 rounded-xl overflow-hidden bg-secondary">
              {c.profile_path && <img src={posterUrl(c.profile_path, "w185")} alt={c.name} loading="lazy" className="h-full w-full object-cover" />}
            </div>
            <p className="text-xs font-semibold mt-1.5 leading-tight line-clamp-2">{c.name}</p>
            <p className="text-[11px] text-muted-foreground leading-tight line-clamp-1">{c.character}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Trailer({ trailerKey }) {
  const [playing, setPlaying] = useState(false);
  if (!trailerKey) return <p className="text-sm text-muted-foreground" data-testid="details-no-trailer">No trailer available.</p>;
  if (playing)
    return (
      <div className="aspect-video rounded-2xl overflow-hidden bg-black" data-testid="details-trailer-player">
        <iframe title="Trailer" className="h-full w-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen
          src={`https://www.youtube-nocookie.com/embed/${trailerKey}?autoplay=1&rel=0`} />
      </div>
    );
  return (
    <button onClick={() => setPlaying(true)} data-testid="details-trailer-button"
      className="w-full h-12 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-95">
      <Play className="h-4 w-4 fill-current" /> Watch trailer
    </button>
  );
}

export function MovieDetails({ movie, onClose }) {
  const [data, setData] = useState(null);
  const id = movie?.movie_id;

  useEffect(() => {
    if (!id) return;
    setData(null);
    api.get(`/movies/${id}/details`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  const m = data || movie;
  return (
    <Dialog open={!!movie} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="block w-[calc(100vw-1.5rem)] max-w-lg p-0 overflow-x-hidden max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="movie-details-dialog">
        {!m && <><DialogTitle className="sr-only">Movie details</DialogTitle><DialogDescription className="sr-only">Movie details</DialogDescription></>}
        {m && (
          <>
            <div className="relative h-56 bg-secondary">
              <img src={posterUrl(m.backdrop_path || m.poster_path, "w780")} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
            </div>
            <div className="px-6 pb-6 -mt-16 relative space-y-5 min-w-0">
              <div>
                <DialogTitle className="text-3xl font-black leading-tight" data-testid="details-title">{m.title}</DialogTitle>
                <DialogDescription asChild>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-2" data-testid="details-meta">
                    <span>{m.year || "—"}</span>
                    {data?.runtime > 0 && <span className="flex items-center gap-1" data-testid="details-runtime"><Clock className="h-3.5 w-3.5" />{fmtRuntime(data.runtime)}</span>}
                    {m.vote_average > 0 && <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{m.vote_average.toFixed(1)}</span>}
                    {m.genres?.length > 0 && <span>{m.genres.join(" · ")}</span>}
                  </div>
                </DialogDescription>
                {data?.tagline && <p className="italic text-sm mt-3">“{data.tagline}”</p>}
              </div>
              {m.overview && <p className="text-sm leading-relaxed text-muted-foreground">{m.overview}</p>}
              {data === null && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              {data && (
                <>
                  {data.directors?.length > 0 && <p className="text-sm" data-testid="details-director"><span className="text-muted-foreground">Directed by </span><span className="font-semibold">{data.directors.join(", ")}</span></p>}
                  <Trailer trailerKey={data.trailer_key} />
                  <WatchOptions movieId={m.movie_id} />
                  <CastRow cast={data.cast} />
                </>
              )}
              {data === false && <p className="text-sm text-destructive">Couldn't load details.</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
